import _ from 'underscore';
import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { Constants as EntityConstants } from '@bingads-webui-clientcenter/entity-utils';

import {
  AddressSelectorOptions,
  emptyAddress,
  defaultBusinessNameData,
  defaultPhoneNumberData,
  addressFormProperties,
} from './constants';

const {
  removeDuplicateAddresses,
  isValid: isValidAddress,
  isValidBusinessName,
  exists,
  areEqual,
} = AddressUtils;

const { enterNewAddressId, selectExistingAddressId } = AddressSelectorOptions;
const { CountryCode } = EntityConstants;

export const getSelectedABL = ({
  addressId,
  addressList,
  countryCode,
  isOptional = false,
}) => {
  const selectedABLFromList = _.findWhere(addressList, { Id: addressId });
  const selectedABL = [enterNewAddressId, selectExistingAddressId].includes(addressId)
    ? { ...emptyAddress, Country: countryCode }
    : selectedABLFromList;

  const selectedAddress = selectedABL || {};
  const selectedBusinessName = (selectedABL && selectedABL.BusinessName) || '';

  const isSelectedAddressValid = isValidAddress(selectedAddress, { isOptional });
  const isSelectedBusinessNameValid = isValidBusinessName(selectedABL, { isOptional });
  const isSelectedABLFromList = !!selectedABLFromList;

  return {
    selectedAddress,
    selectedBusinessName,
    isSelectedAddressValid,
    isSelectedBusinessNameValid,
    isSelectedABLFromList,
  };
};

// This logic is initially moved from Campaign
// Source code: https://msasg.visualstudio.com/Bing_Ads/_git/AdsAppsCampaignUI?path=%2Fprivate%2Fui-next%2Fpackages%2Faccount-creation%2Fsrc%2Fprefill-business-address.js&version=GBmaster&line=9&lineEnd=15&lineStartColumn=1&lineEndColumn=2&lineStyle=plain&_a=contents
// Try to include the includedAddressId in the result list
export const formatAddressOptions = ({
  addressList,
  currentCountry,
  includedAddressIds,
}) => {
  const filteredList = _.filter(addressList, address => address.Id && address.Country === currentCountry && (!address.IsDeleted || includedAddressIds.includes(address.Id)));
  const deduplicateList = removeDuplicateAddresses(filteredList, { includedAddressIds });
  return _.map(deduplicateList, ({ Id, formatted }) => ({ key: Id, text: formatted }));
};

export const areAddressesEqual = (address1, address2) => areEqual(
  address1, address2,
  { propertiesToCompare: addressFormProperties }
);

export const initialAddressDataGetter = ({ addressData, countryCode }) => {
  const initialAddress = exists((addressData || {}).address) ? addressData.address : { Country: countryCode };
  const isInitialAddressValid = AddressUtils.isValid(initialAddress);

  return {
    address: initialAddress,
    addressWithValidation: null,
    isValid: isInitialAddressValid,
  };
};

export const initialUserCountryCodeGetter = userAddressData => _.get(userAddressData || {}, ['address', 'Country']);

export const initialAblInfoStateGetters = {
  getInitialPrefillAddressId: ({ prefillAddressId }) => prefillAddressId || AddressSelectorOptions.enterNewAddressId,
  getInitialBusinessNameData: ({ businessNameData }) => businessNameData || defaultBusinessNameData,
  getInitialPhoneNumberData: ({ phoneNumberData }) => phoneNumberData || defaultPhoneNumberData,
  getInitialCountryCode: ({ countryCode }) => countryCode,
  getInitialUserCountryCode: ({ userAddressData }) => initialUserCountryCodeGetter(userAddressData),
  getInitialAddressData: ({ addressData, countryCode }) => initialAddressDataGetter({ addressData, countryCode }),
  getInitialUserAddressData: ({ userAddressData }) => initialAddressDataGetter({ addressData: userAddressData, countryCode: initialUserCountryCodeGetter(userAddressData) }),
  getInitialIsUseSameLocationChecked: ({
    addressData, userAddressData,
  }) => {
    if (!userAddressData || _.isEmpty(userAddressData.address)) {
      return true;
    }
    return areAddressesEqual((addressData || {}).address, userAddressData.address);
  },
  getInitialTaxData: ({ taxData }) => taxData || null,
  getInitialTimeZoneData: ({ timeZoneData }) => timeZoneData || null,
  getInitialCurrencyData: ({ currencyData }) => currencyData || null,
};

export const hasBillingGroup = ({ accountBillingGroupMap }) =>
  _.any(accountBillingGroupMap, accountBillingGroup => _.get(accountBillingGroup, ['BillingGroup', 'BillingGroupId']) > 0);

export const isIndianAddress = address => _.chain(address).result('Country').value() === CountryCode.IN;
